import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Stack, Card, CardContent, Chip, Button, Tabs, Tab, Alert, LinearProgress, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Link,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import {
  VENDOR_BOOKINGS_QUERY, RESPOND_TO_VENDOR_SELECTION_MUTATION, COMPLETE_VENDOR_SELECTION_MUTATION,
} from '../graphql/queries';
import { BookingStatusChip, fmtBookingDate, countdown, quantityText } from '../components/VendorBooking';

import Paged from '../components/Paged'; // lists beyond 10 are paged
import { AccountManagerContact } from '../components/AccountManagerCard';
import BookingProgress from '../components/BookingProgress';
const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

// Vendor's bookings: requests to answer, upcoming confirmed dates, and history
export default function VendorBookingsPage() {
  const [tab, setTab] = useState('requests');
  const [respond, setRespond] = useState(null); // { booking, response }
  const [note, setNote] = useState('');
  const [message, setMessage] = useState(null);
  const { data, loading, error, refetch } = useQuery(VENDOR_BOOKINGS_QUERY, { fetchPolicy: 'cache-and-network' });
  const [respondMutation, { loading: busy }] = useMutation(RESPOND_TO_VENDOR_SELECTION_MUTATION);
  const [complete] = useMutation(COMPLETE_VENDOR_SELECTION_MUTATION);

  const all = data?.vendorBookings || [];
  const requests = all.filter(b => b.status === 'requested');
  const upcoming = all.filter(b => b.status === 'confirmed');
  const past = all.filter(b => !['requested', 'confirmed'].includes(b.status));
  const lists = { requests, upcoming, past };
  const shown = lists[tab];

  const submitResponse = async () => {
    try {
      await respondMutation({ variables: { id: respond.booking.id, response: respond.response, note: note || null } });
      setMessage({ severity: respond.response === 'confirm' ? 'success' : 'info', text: `Booking ${respond.response === 'confirm' ? 'confirmed' : 'declined'}. ${respond.booking.familyName} has been notified.` });
      setRespond(null); setNote(''); refetch();
    } catch (e) { setMessage({ severity: 'error', text: e.message }); }
  };
  const doComplete = async (b) => {
    try { await complete({ variables: { id: b.id } }); setMessage({ severity: 'success', text: 'Marked as completed.' }); refetch(); }
    catch (e) { setMessage({ severity: 'error', text: e.message }); }
  };

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700}>Bookings</Typography>
        <Typography variant="body2" color="text.secondary">
          Families who selected your services. Confirm or decline requests; you're reminded 30, 7 and 1 day(s) before each date and on the day.
        </Typography>
      </Box>
      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}
      {loading && !data && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="requests" label={`Requests (${requests.length})`} />
        <Tab value="upcoming" label={`Confirmed (${upcoming.length})`} />
        <Tab value="past" label={`Past & closed (${past.length})`} />
      </Tabs>
      {!loading && shown.length === 0 && (
        <Alert severity="info">
          {tab === 'requests' ? 'No new booking requests.' : tab === 'upcoming' ? 'No confirmed bookings.' : 'No past or closed bookings.'}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Paged items={shown}>{rows => rows.map(b => (
          <Grid item xs={12} lg={6} key={b.id}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={700}>{b.familyName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {b.familyRole} · <Link href={`mailto:${b.familyEmail}`}>{b.familyEmail}</Link>
                    </Typography>
                  </Box>
                  <BookingStatusChip status={b.status} forVendor />
                </Stack>
                <Typography variant="body1" fontWeight={600} mt={1.5}>
                  {b.offeringTitle}{b.packageName ? ` · ${b.packageName}` : ''}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap" useFlexGap>
                  <EventIcon fontSize="small" color="action" />
                  <Typography fontWeight={600}>{fmtBookingDate(b.requiredDate)}{b.requiredTime ? `, ${b.requiredTime}` : ''}</Typography>
                  {['requested', 'confirmed'].includes(b.status) && (
                    <Chip size="small" label={countdown(b.daysUntil)} color={b.daysUntil <= 7 ? 'warning' : 'default'} />
                  )}
                </Stack>
                <Typography variant="body2" mt={1}>
                  {b.price != null && <>{inr(b.price)} {b.priceUnit}</>}{b.quantity ? ` · ${quantityText(b.category, b.quantity)}` : ''}
                </Typography>
                {b.notes && <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>Family's notes: {b.notes}</Typography>}
                {/* The family's account manager handles this booking with you */}
                {b.accountManager && (
                  <Box mt={1}><AccountManagerContact manager={b.accountManager} label="Point of contact (account manager)" /></Box>
                )}
                {/* Confirm each stage before the function, with vouchers and photos */}
                <BookingProgress booking={b} forVendor onMessage={(text) => setMessage({ severity: 'success', text })} />
                {b.vendorNote && <Typography variant="body2" color="text.secondary" mt={0.5}>Your note: {b.vendorNote}</Typography>}
                <Stack direction="row" spacing={1} mt={2}>
                  {b.status === 'requested' && (
                    <>
                      <Button size="small" variant="contained" color="success" onClick={() => { setRespond({ booking: b, response: 'confirm' }); setNote(''); }}>Confirm</Button>
                      <Button size="small" color="error" onClick={() => { setRespond({ booking: b, response: 'decline' }); setNote(''); }}>Decline</Button>
                    </>
                  )}
                  {b.status === 'confirmed' && (
                    <>
                      <Button size="small" color="error" onClick={() => { setRespond({ booking: b, response: 'decline' }); setNote(''); }}>Can't do it after all</Button>
                      {b.daysUntil <= 0 && <Button size="small" variant="outlined" onClick={() => doComplete(b)}>Mark completed</Button>}
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}</Paged>
      </Grid>

      <Dialog open={Boolean(respond)} onClose={() => setRespond(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{respond?.response === 'confirm' ? 'Confirm booking' : 'Decline booking'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            {respond?.booking.familyName} · {respond?.booking.offeringTitle} on {respond && fmtBookingDate(respond.booking.requiredDate)}. The family will be notified.
          </Typography>
          <TextField
            label={respond?.response === 'confirm' ? 'Message to the family (optional)' : 'Reason (required)'}
            value={note} onChange={e => setNote(e.target.value)} fullWidth multiline rows={3} size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRespond(null)}>Back</Button>
          <Button variant="contained" color={respond?.response === 'confirm' ? 'success' : 'error'} onClick={submitResponse}
            disabled={busy || (respond?.response === 'decline' && !note.trim())}>
            {respond?.response === 'confirm' ? 'Confirm' : 'Decline'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
